const { getElement, getElements } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  fauxBtn: '[data-product-form-el="fauxBtn"]',
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  backInStockTrigger: '[data-product-bis-trigger]',
  stockMessageContainer: '[data-product-stock-message-container]',
  stockText: '[data-product-stock-text]',
  stockIcon: '[data-product-stock-icon]',
  form: '[data-product-form]',
  price: '[data-product-price]',
  sku: '[data-product-sku]',
  variantIndicator: '[data-product-variant-indicator]',
}

class Variant {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
  }

  async setPrice (context) {
    await window.BAO.sectionRenderer
      .fetch(
        'product-price',
        `${context.dataset.url}?variant=${this.data.id}&section_id=product-price`,
      )
      .then((res) => {
        const newDOM = window.BAO.sectionRenderer.convertToDOM(
          res['product-price'],
        )
        const myself = newDOM.querySelector('.shopify-section')

        const priceElements = getElements(
          `${selectors.price}[data-form-id='${context.dataset.formId}']`,
        )
        priceElements.forEach((el) => {
          el.innerHTML = myself.innerHTML
        })

        const variantPriceElPreviewLaunch = document.querySelector(
          '[data-variant-radios-external-el="variantPrice"]',
        )
        if (variantPriceElPreviewLaunch) {
          variantPriceElPreviewLaunch.innerHTML = myself.innerHTML
        }
      })
  }

  setAvailability (context) {
    getElements(`${selectors.backInStockTrigger}`).forEach((el) => {
      el.setAttribute('aria-hidden', this.data.available)

      const url = window.location.href
      if (!url.includes('/collections/')) {
        el.setAttribute(
          'data-product-url',
          `${this.url.toString()}?variant=${this.data.id}`,
        )
      }
    })

    getElements(
      `${selectors.addToCart}[data-form-id='${context.dataset.formId}']`,
    ).forEach((el) => {
      el.disabled = !this.data.available
      el.setAttribute('aria-hidden', !this.data.available)
    })

    getElements(
      `${selectors.addToCartText}[data-form-id='${context.dataset.formId}']`,
    ).forEach(
      (el) =>
        (el.textContent = this.data.available
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut),
    )
    getElements(
      `${selectors.stockText}[data-form-id='${context.dataset.formId}']`,
    ).forEach(
      (el) =>
        (el.textContent = this.data.available
          ? window.theme.strings.inStock
          : window.theme.strings.outStock),
    )
    getElements(
      `${selectors.stockIcon}[data-form-id='${context.dataset.formId}']`,
    ).forEach(
      (el) =>
        (el.innerHTML = this.data.available
          ? `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M4.33333 7.91667L6.41667 10L11 5" stroke="currentColor" /><rect x="0.5" y="0.5" width="14" height="14" rx="7" stroke="currentColor" /></svg>`
          : `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M5 10L10 5" stroke="currentColor" /><path d="M5 5L10 10" stroke="currentColor" /><rect x="0.5" y="0.5" width="14" height="14" rx="7" stroke="currentColor" /></svg>`),
    )
  }

  updateStockWarning (context) {
    const variantId = this.data.id

    const variantInventoryMessages = getElements(
      '[data-variant-inventory-message]',
    )

    if (variantInventoryMessages.length === 0) return

    variantInventoryMessages.forEach((variantInventoryMessage) => {
      const variantMessageId = parseInt(
        variantInventoryMessage.dataset.variantId,
        10,
      )

      if (variantMessageId === variantId) {
        const variantInventory = parseInt(
          variantInventoryMessage.dataset.variantInventory,
          10,
        )
        const stockLevelTrigger = parseInt(
          variantInventoryMessage.dataset.variantInventoryTrigger,
          10,
        )

        variantInventoryMessage.parentElement.setAttribute(
          'aria-hidden',
          stockLevelTrigger > variantInventory ? 'false' : 'true',
        )
      } else {
        variantInventoryMessage.parentElement.setAttribute(
          'aria-hidden',
          'true',
        )
      }
    })
  }

  updateInput (context) {
    const inputs = getElements(
      `form[action*="/cart/add"][id="${context.dataset.formId}"] [name="id"]`,
    )

    inputs.forEach((input) => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  updateIndicator (name, value, context) {
    const variantIndicators = getElements(
      `${selectors.variantIndicator}[data-form-id='${context.dataset.formId}']`,
    )

    const variantIndicator = variantIndicators.find(
      (el) => el.dataset.name === name,
    )

    if (!variantIndicator) return

    variantIndicator.textContent = value

    if (name.toLowerCase() === 'size') {
      variantIndicator.textContent = window.sizeStrings[value.toLowerCase()]
        ? window.sizeStrings[value.toLowerCase()]
        : value
    } else {
      variantIndicator.textContent = value
    }

    const variantSizeElPreviewLaunch = document.querySelector(
      '[data-variant-radios-external-el="variantName"]',
    )

    if (variantSizeElPreviewLaunch) {
      variantSizeElPreviewLaunch.innerHTML = value
    }
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach((shareButton) => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.searchParams.set('variant', this.id.toString())

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant)]
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)
  }

  onVariantChange (e) {
    const variant = this.getVariant()

    if (this.currentVariant) {
      variant.setAvailability(this)
      variant.updateStockWarning(this)
      variant.setPrice(this)
      variant.updateInput(this)
      variant.updateIndicator(e.target.name, e.target.dataset.value, this)
      getElement(selectors.stockMessageContainer)?.setAttribute(
        'aria-hidden',
        'false',
      )
    }

    if (this.updateUrl) {
      if (!this.currentVariant) {
        this.resetPage(variant)

        return
      }

      const fauxBtn = getElement(selectors.fauxBtn)
      if (fauxBtn) {
        fauxBtn.parentNode.removeChild(fauxBtn)
      }

      const url = window.location.href

      if (!url.includes('/collections/')) {
        variant.setUrl()
        variant.setShareUrl()
      }

      if (document.querySelector('[data-preview-launch-signup-el="form"]')) {
        document
          .querySelector('.prd-BackInStock_Form')
          .setAttribute('data-variant-id', variant.data.id)

        // on PDP open the preview launch modal
        if (
          e.target.getAttribute('data-preview-launch-external-el') ===
          'size-trigger'
        ) {
          document
            .querySelector('[data-drawers-trigger="preview-launch"]')
            .click()
        }
      }

      if (document.querySelector('[data-wishlist-modal-el="submit"]')) {
        document
          .querySelector('[data-wishlist-modal-el="submit"]')
          .setAttribute('data-variant-id', variant.data.id)
      }
    }
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data, this)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      (variant) => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  get formId () {
    return this.dataset.formId
  }

  get updateUrl () {
    return this.dataset.updateUrl === 'true'
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find((variant) => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find((el) => el.checked)?.value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}
